import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/lib/i18n";
import { LANGS } from "@/lib/i18n-extra";

const links = [["/", "home"], ["/nehnutelnosti", "properties"], ["/destinacie", "destinations"], ["/spolupraca", "partner"], ["/blog", "blog"], ["/o-nas", "about"], ["/kontakt", "contact"]];

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button data-testid="lang-switch" onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-[11px] tracking-[0.25em] text-ivory/90 hover:text-gold transition-colors py-2">
        <Globe size={14} strokeWidth={1.3} />
        <span className="text-gold">{lang.toUpperCase()}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} data-testid="lang-menu"
            className="absolute end-0 top-full bg-midnight/95 backdrop-blur-xl border border-gold/30 py-2 min-w-[150px] z-50">
            {LANGS.map(([code, short, name]) => (
              <li key={code}>
                <button onClick={() => { setLang(code); setOpen(false); }} data-testid={`lang-option-${code}`}
                  className={`w-full text-start px-4 py-2 text-sm flex justify-between gap-4 hover:text-gold ${lang === code ? "text-gold" : "text-ivory/85"}`}>
                  <span>{name}</span><span className="text-[10px] tracking-[0.2em] text-ivory/50">{short}</span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,padding,backdrop-filter] duration-500 ${scrolled || open ? "bg-midnight/90 backdrop-blur-xl py-3" : "bg-gradient-to-b from-midnight/70 to-transparent py-6"}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" onClick={() => setOpen(false)}><Logo size="sm" /></Link>
        <nav className="hidden xl:flex items-center gap-7">
          {links.map(([to, k]) => (
            <NavLink key={to} to={to} end data-testid={`nav-${k}`} className={({ isActive }) => `nav-link text-[11px] tracking-[0.28em] uppercase ${isActive ? "text-gold is-active" : "text-ivory/90"}`}>
              {t.nav[k] || "Blog"}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-6">
          <LangSwitch />
          <button className="xl:hidden text-ivory" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X strokeWidth={1.2} /> : <Menu strokeWidth={1.2} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="xl:hidden overflow-hidden" data-testid="mobile-menu">
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map(([to, k], i) => (
                <motion.div key={to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Link to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${k}`} className="font-display text-3xl text-ivory">{t.nav[k] || "Blog"}</Link>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
